import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Grid,
  TextField,
  InputAdornment,
  Chip,
  Paper,
  Container,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../../contexts/AuthContext';
import { listUsers } from '../../services/userManagementService';
import { createAppointment } from '../../services/appointmentsService';
import { getAvailableTimeSlots } from '../../services/schedulesService';
import SkeletonLoader from '../shared/SkeletonLoader';
import EmptyState from '../shared/EmptyState';
import ErrorMessage from '../shared/ErrorMessage';
import toast from 'react-hot-toast';

/**
 * PhysicianDirectory Component
 * Allows patients to browse available physicians and request appointments
 */
const PhysicianDirectory = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [physicians, setPhysicians] = useState([]);
  const [filteredPhysicians, setFilteredPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [requestNotes, setRequestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch physicians
  useEffect(() => {
    const fetchPhysicians = async () => {
      if (!user?.tenant_id) return;

      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await listUsers({
          role: 'physician',
          tenantId: user.tenant_id,
          page: 1,
          pageSize: 100,
        });

        if (fetchError) {
          throw fetchError;
        }

        const physiciansList = data?.rows || data?.data || [];
        setPhysicians(physiciansList);
        setFilteredPhysicians(physiciansList);
      } catch (err) {
        console.error('Error fetching physicians:', err);
        setError('Failed to load physicians. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPhysicians();
  }, [user]);

  // Filter physicians based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPhysicians(physicians);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = physicians.filter(physician => {
      const firstName = (physician.first_name || '').toLowerCase();
      const lastName = (physician.last_name || '').toLowerCase();
      const email = (physician.email || '').toLowerCase();
      const specialty = (physician.specialty || '').toLowerCase();
      
      return firstName.includes(query) || 
             lastName.includes(query) || 
             email.includes(query) ||
             specialty.includes(query);
    });

    setFilteredPhysicians(filtered);
  }, [searchQuery, physicians]);

  const handleRequestAppointment = async (physician) => {
    setSelectedPhysician(physician);
    setRequestModalOpen(true);
    setSelectedDate(null);
    setSelectedTime(null);
    setRequestNotes('');
    setAvailableSlots([]);
    
    // Fetch available slots for the next 14 days
    if (physician?.id) {
      setLoadingSlots(true);
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 14);
      
      const startDateStr = today.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      const { data: slots, error } = await getAvailableTimeSlots(
        physician.id,
        startDateStr,
        endDateStr,
        30
      );
      
      if (!error && slots) {
        setAvailableSlots(slots);
      }
      setLoadingSlots(false);
    }
  };
  
  // Fetch available slots when date changes
  useEffect(() => {
    const fetchSlotsForDate = async () => {
      if (!selectedPhysician?.id || !selectedDate) {
        setAvailableSlots([]);
        return;
      }
      
      setLoadingSlots(true);
      const dateStr = selectedDate.toISOString().split('T')[0];
      
      const { data: slots, error } = await getAvailableTimeSlots(
        selectedPhysician.id,
        dateStr,
        dateStr,
        30
      );
      
      if (!error && slots) {
        // Filter slots for the selected date
        const dateSlots = slots.filter(slot => slot.date === dateStr);
        setAvailableSlots(dateSlots);
      } else {
        setAvailableSlots([]);
      }
      setLoadingSlots(false);
    };
    
    fetchSlotsForDate();
  }, [selectedDate, selectedPhysician]);

  const handleSubmitRequest = async () => {
    if (!selectedDate || !selectedTime || !selectedPhysician) {
      toast.error('Please select a date and time');
      return;
    }

    setSubmitting(true);

    try {
      // Combine date and time
      const dateTime = new Date(selectedDate);
      const [hours, minutes] = selectedTime.split(':');
      dateTime.setHours(parseInt(hours, 10));
      dateTime.setMinutes(parseInt(minutes, 10));

      const appointmentData = {
        patient_id: user.id,
        provider_id: selectedPhysician.id,
        scheduled_time: dateTime.toISOString(),
        tenant_id: user.tenant_id,
        status: 'pending', // Patient requests are pending
        notes: requestNotes.trim() || undefined,
      };

      const { error: createError } = await createAppointment(appointmentData);

      if (createError) {
        throw createError;
      }

      toast.success('Appointment request sent! The physician will review and respond.');
      setRequestModalOpen(false);
      setSelectedPhysician(null);
      setSelectedDate(null);
      setSelectedTime(null);
      setRequestNotes('');
    } catch (err) {
      console.error('Error requesting appointment:', err);
      toast.error(err.message || 'Failed to request appointment');
    } finally {
      setSubmitting(false);
    }
  };

  const getPhysicianName = (physician) => {
    return `${physician.first_name || ''} ${physician.last_name || ''}`.trim() || physician.email || 'Unknown';
  };

  const getInitials = (physician) => {
    const name = getPhysicianName(physician);
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Find a Physician
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Browse available physicians and request an appointment
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          placeholder="Search by name, specialty, or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
            },
          }}
        />
      </Box>

      {/* Physicians List */}
      {loading ? (
        <Box sx={{ py: 4 }}>
          <SkeletonLoader variant="card" count={3} height={200} />
        </Box>
      ) : error ? (
        <ErrorMessage
          title="Unable to load physicians"
          message={error}
          actionLabel="Retry"
          onAction={() => navigate(0)}
          severity="error"
        />
      ) : filteredPhysicians.length === 0 ? (
        <EmptyState
          title={searchQuery ? "No physicians found" : "No physicians available"}
          description={searchQuery 
            ? "Try adjusting your search terms" 
            : "There are no physicians available in your clinic at this time."}
          icon={<PersonIcon />}
        />
      ) : (
        <Grid container spacing={3}>
          {filteredPhysicians.map((physician) => (
            <Grid item xs={12} sm={6} md={4} key={physician.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        bgcolor: 'primary.main',
                        fontSize: 24,
                        mr: 2,
                      }}
                    >
                      {getInitials(physician)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {getPhysicianName(physician)}
                      </Typography>
                      {physician.specialty && (
                        <Chip
                          label={physician.specialty}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>

                  {physician.email && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {physician.email}
                    </Typography>
                  )}

                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Available for appointments
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<CalendarMonthIcon />}
                    onClick={() => handleRequestAppointment(physician)}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Request Appointment
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Request Appointment Modal */}
      <Dialog
        open={requestModalOpen}
        onClose={() => !submitting && setRequestModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request Appointment with {selectedPhysician ? getPhysicianName(selectedPhysician) : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={setSelectedDate}
                minDate={new Date()}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  }
                }}
              />
            </LocalizationProvider>

            <FormControl fullWidth>
              <InputLabel>Select Time</InputLabel>
              <Select
                value={selectedTime || ''}
                label="Select Time"
                onChange={(e) => setSelectedTime(e.target.value)}
                disabled={!selectedDate || loadingSlots}
              >
                {loadingSlots ? (
                  <MenuItem disabled>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      Loading available times...
                    </Box>
                  </MenuItem>
                ) : availableSlots.length === 0 ? (
                  <MenuItem disabled>
                    {selectedDate 
                      ? 'No available time slots for this date' 
                      : 'Please select a date first'}
                  </MenuItem>
                ) : (
                  availableSlots.map((slot) => {
                    const timeValue = slot.start_time;
                    const displayTime = `${slot.start_time} - ${slot.end_time}`;
                    return (
                      <MenuItem key={timeValue} value={timeValue}>
                        {displayTime}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
              {selectedDate && availableSlots.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                  {availableSlots.length} available slot{availableSlots.length !== 1 ? 's' : ''} on this date
                </Typography>
              )}
            </FormControl>

            <TextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              placeholder="Any additional information for the physician..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRequestModalOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={submitting || !selectedDate || !selectedTime}
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PhysicianDirectory;

